import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * User-preferences context — theme + homepage layout + default filters.
 *
 * Reads once on login, writes-through on any update. Falls back to
 * localStorage cache so the first paint has the right theme even before
 * the server responds.
 */
const DEFAULTS = {
    theme: "light",
    compact_view: false,
    default_month: null,
    default_owner: "all",
    homepage_hidden_widgets: [],
    homepage_section_order: [],
    pinned_modules: [],
    hide_balance: false,
};

const PrefsCtx = createContext({ prefs: DEFAULTS, updatePrefs: async () => {}, toggleWidget: () => {}, pinModule: () => {} });
export const usePrefs = () => useContext(PrefsCtx);

const LS_KEY = "vault.prefs";

function readLocal() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return DEFAULTS;
        return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch { return DEFAULTS; }
}
function writeLocal(p) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch { /* noop */ }
}

function applyTheme(theme) {
    const root = document.documentElement;
    const effective = theme === "system"
        ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : theme;
    root.classList.toggle("dark", effective === "dark");
    root.setAttribute("data-theme", effective);
}

export function PrefsProvider({ children }) {
    const { user } = useAuth();
    const [prefs, setPrefs] = useState(readLocal);

    // Apply theme immediately on first mount
    useEffect(() => { applyTheme(prefs.theme); }, [prefs.theme]);

    // Apply hide-balance root class so CSS can blur monetary values everywhere
    useEffect(() => {
        document.documentElement.classList.toggle("vault-hide-balance", !!prefs.hide_balance);
    }, [prefs.hide_balance]);

    // Global keyboard shortcut: Shift+H toggles hide-balance mode.
    useEffect(() => {
        // First-run discoverability toast — positioned bottom so it doesn't
        // sit under the topbar notification bell for the first 4-6 seconds.
        try {
            if (!localStorage.getItem("vault.shifth.discovered")) {
                setTimeout(() => {
                    import("sonner").then(({ toast }) => {
                        toast("Tip: press Shift + H to blur every balance instantly.", {
                            duration: 6000,
                            id: "shifth-tip",
                            position: "bottom-right",
                        });
                    });
                    localStorage.setItem("vault.shifth.discovered", "1");
                }, 4000);
            }
        } catch { /* noop */ }

        const onKey = (e) => {
            if (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey &&
                (e.key === "H" || e.key === "h") &&
                !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) {
                e.preventDefault();
                setPrefs((p) => {
                    const next = { ...p, hide_balance: !p.hide_balance };
                    writeLocal(next);
                    return next;
                });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Load from server after auth
    useEffect(() => {
        if (!user?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/user/preferences");
                if (cancelled) return;
                const merged = { ...DEFAULTS, ...r.data };
                setPrefs(merged);
                writeLocal(merged);
                applyTheme(merged.theme);
            } catch { /* silent — LS fallback is fine */ }
        })();
        return () => { cancelled = true; };
    }, [user?.id]);

    const updatePrefs = useCallback(async (patch) => {
        const next = { ...prefs, ...patch };
        setPrefs(next);
        writeLocal(next);
        if (patch.theme !== undefined) applyTheme(next.theme);
        if (user?.id) {
            try { await api.patch("/user/preferences", { prefs: patch }); } catch { /* keep local anyway */ }
        }
    }, [prefs, user?.id]);

    const toggleWidget = useCallback((key) => {
        const hidden = new Set(prefs.homepage_hidden_widgets || []);
        if (hidden.has(key)) hidden.delete(key); else hidden.add(key);
        updatePrefs({ homepage_hidden_widgets: Array.from(hidden) });
    }, [prefs.homepage_hidden_widgets, updatePrefs]);

    const pinModule = useCallback((slug) => {
        const pinned = new Set(prefs.pinned_modules || []);
        if (pinned.has(slug)) pinned.delete(slug); else pinned.add(slug);
        updatePrefs({ pinned_modules: Array.from(pinned) });
    }, [prefs.pinned_modules, updatePrefs]);

    return (
        <PrefsCtx.Provider value={{ prefs, updatePrefs, toggleWidget, pinModule }}>
            {children}
        </PrefsCtx.Provider>
    );
}
